import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Grid from '../../../Grid';
import {
    Container, Row, Col, Button, Card, CardBody, CardTitle, Collapse, FormGroup, Label, Input, Modal
} from "reactstrap";
import Loader from "../../../loaders";
import _ from "lodash";
import { DatePicker } from '../../../date-pickers';
import InputMask from "react-input-mask";
import fileDownload from 'js-file-download';
import api from "../../../../services/api";
import { FileDropzone, MultipleDropzoneList } from '../../../FileDropzone';
import { DropdownBox } from '../../../dropdowns';
import { formatDate, formatDateZeroTime } from '../../../../utils';
import { v4 } from 'uuid';

const apiUrl = "";

export default function AddEmployeeDialog(props) {
    const {
        lookups,
        onClose,
        onSave,
        data,
    } = props;
    const [loading, setLoading] = useState(false);
    const [itemInEdit, setItemInEdit] = useState(data ?? {uuid: v4()});

    const handleTextChanged = ({ target: { name, value } }) => {
        setItemInEdit({ ...itemInEdit, candidate: {...itemInEdit?.candidate, [name]: value} });
    }

    const downloadAttachment = (fileInfo) => {
        if (fileInfo.file) {
            fileDownload(fileInfo.file, fileInfo.file.name);
            return;
        }
        
        setLoading(true);
        const url = `${apiUrl}/document/${fileInfo.candidateDocumentId}`;
        api.get(url, { responseType: 'blob' })
            .then(blob => {
                fileDownload(blob.data, fileInfo.filename);
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }

    const removeAttachment = useCallback((fileInfo) => {
        const newAttachments = [...itemInEdit?.candidate?.candidateDocuments ?? []];
        const removedItem = newAttachments.filter(a => a.candidateDocumentId === fileInfo.candidateDocumentId)[0];
        const removedIndex = newAttachments.indexOf(removedItem);
        newAttachments.splice(removedIndex, 1);
        setItemInEdit({...itemInEdit, candidate: {...itemInEdit.candidate, candidateDocuments: newAttachments}});
    }, [itemInEdit]);

    const files = useMemo(() => {
        if (itemInEdit?.candidate?.candidateDocuments?.length > 0)
            return <MultipleDropzoneList
                idField="candidateDocumentId"
                nameField="filename"
                sizeField="fileSizeInKbytes"
                download={downloadAttachment}
                remove={removeAttachment}
                data={itemInEdit?.candidate?.candidateDocuments ?? []}
                badgeTextField="approverRole"
            />;

        return null;
    }, [itemInEdit]);

    return (
        <>
            <Modal
                isOpen={true}
                className="modal-lg m-4"
                modalClassName="db-example-modal-lg"
            >
                {(loading) && <Loader />}
                <div
                    className="modal-header"
                >
                    <h5
                        className="modal-title"
                    >
                        Add Candidate
                    </h5>
                    <button
                        aria-label="Close"
                        className="close"
                        onClick={onClose}
                        type="button"
                    >
                        <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                    </button>
                </div>
                <div
                    className="modal-body"
                >
                    <Container fluid className="w-100">
                        <Row className="w-100 m-0 p-0">
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        First Name
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        value={itemInEdit?.candidate?.firstName ?? ""}
                                        name="firstName"
                                        id="firstName"
                                        onChange={handleTextChanged}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Surname
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        value={itemInEdit?.candidate?.surname ?? ""}
                                        name="surname"
                                        id="surname"
                                        onChange={handleTextChanged}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Salutation
                                    </Label>
                                    <DropdownBox
                                        data={lookups.salutations}
                                        selectedItem={itemInEdit?.candidate?.salutation ?? null}
                                        onChange={(item) => {
                                            setItemInEdit({ ...itemInEdit, candidate: {...itemInEdit?.candidate, salutation: item, salutationId: item ? item.lookupId : null} });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Gender
                                    </Label>
                                    <DropdownBox
                                        data={lookups.genders}
                                        selectedItem={itemInEdit?.candidate?.gender ?? null}
                                        onChange={(item) => {
                                            setItemInEdit({ ...itemInEdit, candidate: {...itemInEdit?.candidate, gender: item, genderId: item ? item.lookupId : null} });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Date of Birth
                                    </Label>
                                    <DatePicker
                                        name="dob"
                                        id="dob"
                                        required
                                        value={formatDate(itemInEdit?.candidate?.dob)}
                                        onChange={(value) => !value? null : setItemInEdit({ ...itemInEdit, candidate: {...itemInEdit?.candidate, dob: formatDateZeroTime(value)} })}
                                        closeOnSelect
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Mobile Number
                                    </Label>
                                    <InputMask
                                        name="mobilePhoneNumber"
                                        id="mobilePhoneNumber"
                                        type="tel"
                                        required
                                        value={itemInEdit?.candidate?.mobilePhoneNumber ?? ""}
                                        onChange={handleTextChanged}
                                        className="form-control"
                                        mask={process.env.REACT_APP_MOBILE_FORMAT}
                                        alwaysShowMask={false}
                                        formatChars={{ '0': '[0-9X]' }}
                                        onFocus={(event) => event.currentTarget.select()}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12} lg={4}>
                                <FormGroup>
                                    <Label>
                                        Email
                                    </Label>
                                    <Input
                                        name="email"
                                        id="email"
                                        type="email"
                                        required
                                        //readOnly
                                        value={itemInEdit?.candidate?.email ?? ""}
                                        onChange={handleTextChanged}
                                    />
                                </FormGroup>
                            </Col>
                            <Col lg={12}>
                                <FormGroup>
                                    <Label>
                                        CV
                                    </Label>
                                    <FileDropzone
                                        files={files}
                                        onDrop={([file]) => {
                                            const newAttachments = itemInEdit?.candidate?.candidateDocuments?.length ? [...itemInEdit?.candidate?.candidateDocuments] : [];
                                            const newIds = newAttachments?.filter(a => a.candidateDocumentId <= 0);
                                            if (newIds.length)
                                                newIds.sort((a, b) => parseInt(b.candidateDocumentId) - parseInt(a.candidateDocumentId));

                                            const newId = newIds.length ? newIds[0].candidateDocumentId - 1 : 0;
                                            const newData = {
                                                candidateId: itemInEdit.candidateId ?? 0,
                                                candidateDocumentId: newId,
                                                file,
                                                uuid: v4(),
                                            };
                                            newAttachments.push(newData);
                                            setItemInEdit({...itemInEdit, candidate: {...itemInEdit?.candidate, candidateDocuments: newAttachments}});
                                        }}
                                        maxFiles={0}
                                        disabled={itemInEdit.statusName === "Approved"}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Container>
                </div>
                <div
                    className="modal-footer"
                >
                    <Button
                        color="secondary"
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </Button>
                    <Button
                        color="default"
                        type="button"
                        onClick={() => {
                            onSave(itemInEdit);
                        }}
                        disabled={!itemInEdit?.candidate?.firstName || !itemInEdit?.candidate?.surname}
                    >
                        Add
                    </Button>
                </div>

            </Modal>
        </>
    )

}
